import { Link } from "react-router";
import { css } from "styled-system/css";
import { ed } from "~/components/editorial";

const REPORTS = [
  {
    to: "/delivery",
    kicker: "Engineering",
    title: "Engineering delivery",
    detail:
      "Merged PRs and review coverage: an overview with the story, trends by week, month or quarter, this quarter's pace, and breakdowns by org, product and repo.",
    cadence: "Month or quarter",
  },
  {
    to: "/cost-capitalization",
    kicker: "Finance",
    title: "Cost capitalization",
    detail:
      "Capitalizable and non-capitalizable engineering hours for finance, with a CSV export.",
    cadence: "CSV export",
  },
  {
    to: "/settings/board-reporting",
    kicker: "Board",
    title: "Board reporting",
    detail:
      "A product and delivery schedule for board updates, drafted by a language model.",
    cadence: "Drafted",
  },
  {
    to: "/settings/slack",
    kicker: "Leadership",
    title: "Slack briefings",
    detail: "Scheduled engineering briefings delivered to Slack.",
    cadence: "Scheduled",
  },
] as const;

// Hub for leadership-facing reports: finance, board, and Slack outputs.
export function ReportsPage() {
  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <header className={ed.masthead}>
          <span className={ed.mastTitle}>Reports</span>
          <span className={ed.mastMeta}>{REPORTS.length} reports</span>
        </header>

        <div className={ed.kicker}>For finance, the board and leadership</div>
        <h1 className={ed.headline}>Reports</h1>
        <p className={ed.dek}>
          Everything here exports to a file or arrives on a schedule.
        </p>

        <div className={ed.sect}>The reports</div>
        <ol className={styles.list}>
          {REPORTS.map((r) => (
            <li key={r.to} className={styles.row}>
              <div className={styles.cat}>{r.kicker}</div>
              <div className={styles.body}>
                <h2 className={styles.title}>
                  <Link to={r.to}>{r.title}</Link>
                </h2>
                <p className={styles.detail}>{r.detail}</p>
              </div>
              <div className={styles.side}>
                <span className={styles.cadence}>{r.cadence}</span>
                <Link to={r.to} className={styles.action}>
                  Open →
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

const styles = {
  list: css({ listStyle: "none", m: 0, p: 0 }),
  row: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "140px minmax(0, 1fr) auto" },
    gap: { base: "4px", md: "20px" },
    alignItems: "baseline",
    py: "16px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
  }),
  cat: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink3",
  }),
  body: css({ minWidth: 0 }),
  title: css({
    fontFamily: "serif",
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1.2,
    m: 0,
    "& a": {
      textDecoration: "none",
      _hover: { textDecoration: "underline", textUnderlineOffset: "3px" },
    },
  }),
  detail: css({
    fontFamily: "serif",
    fontSize: "16px",
    lineHeight: 1.5,
    color: "ed.ink2",
    m: 0,
    mt: "4px",
    maxWidth: "640px",
  }),
  side: css({
    display: "flex",
    flexDirection: { base: "row", md: "column" },
    alignItems: { base: "baseline", md: "flex-end" },
    gap: "6px 14px",
  }),
  cadence: css({
    fontFamily: "sans",
    fontSize: "11px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "ed.ink3",
    whiteSpace: "nowrap",
  }),
  action: css({
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    color: "ed.ink",
    whiteSpace: "nowrap",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    textDecorationColor: "ed.rule",
    _hover: { textDecorationColor: "ed.ink" },
  }),
};
